import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const ORANGE = '#FF7900';
const fmtDur = s => (s == null ? '' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`);

function HookRow({ hook, onRename, onSetType, onToggleUniversal, onDelete, onReupload }) {
  const [dur, setDur] = useState(null);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const isUniversal = (hook.tags || []).includes('all') || (hook.tags || []).includes('Universal');
  const broken = hook.fileOk === false;
  const src = `${API}/api/hook/stream/${hook.serverFilename}`;
  const over = dur != null && dur > 10;

  const togglePlay = () => {
    if (broken || !videoRef.current) return;
    if (playing) { videoRef.current.pause(); setPlaying(false); }
    else { videoRef.current.play(); setPlaying(true); }
  };

  const typeBtn = (val, label) => {
    const active = (hook.hookType || 'visual') === val;
    return (
      <button onClick={() => onSetType(hook, val)} style={{
        fontSize: 11, padding: '3px 9px', cursor: 'pointer',
        border: `1px solid ${active ? 'var(--border)' : 'transparent'}`,
        background: active ? 'rgba(0,0,0,0.04)' : 'transparent',
        color: active ? 'var(--text)' : 'var(--text-muted)', borderRadius: 6,
      }}>{label}</button>
    );
  };

  return (
    <div style={{
      display: 'flex', gap: 12, padding: 10, borderRadius: 12,
      border: `1px solid ${broken ? '#e24b4a' : 'var(--border)'}`, background: '#fff',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <div onClick={togglePlay} style={{
          width: 44, height: 62, borderRadius: 6, overflow: 'hidden', background: '#f1efe8',
          border: '1px solid var(--border)', cursor: broken ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {broken ? <span style={{ fontSize: 18, color: 'var(--text-muted)' }}>⚠</span> : (
            <video ref={videoRef} src={src} muted playsInline preload="metadata"
              onLoadedMetadata={e => setDur(e.target.duration)}
              onEnded={() => setPlaying(false)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>
        <span style={{ fontSize: 11, color: over ? ORANGE : 'var(--text-muted)' }}>{broken ? '—' : fmtDur(dur)}</span>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
            {typeBtn('visual', 'Visual')}{typeBtn('talking', 'Talking')}
          </div>
          <button onClick={() => onToggleUniversal(hook)} title={isUniversal ? 'Shown in all features' : 'Only in this feature'} style={{
            fontSize: 11, padding: '3px 10px', cursor: 'pointer', borderRadius: 6,
            border: `1px solid ${isUniversal ? 'transparent' : 'var(--border)'}`,
            background: isUniversal ? 'rgba(255,121,0,0.12)' : 'transparent',
            color: isUniversal ? ORANGE : 'var(--text-muted)',
          }}>Universal</button>
          <span style={{ flex: 1 }} />
          <span title={broken ? 'No file on storage — re-upload before use' : 'File on storage'}
            style={{ fontSize: 14, color: broken ? '#e24b4a' : '#1d9e75', cursor: 'default' }}>
            {broken ? '⚠' : '✓'}
          </span>
          <button onClick={() => onDelete(hook.serverFilename)} aria-label="Delete"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 15 }}>🗑</button>
        </div>

        <input value={hook.name || ''} onChange={e => onRename(hook.serverFilename, e.target.value)}
          style={{ width: '100%', height: 32, fontSize: 13, padding: '0 8px', border: '1px solid var(--border)', borderRadius: 6 }} />

        {broken && (
          <button onClick={() => onReupload(hook)} style={{
            alignSelf: 'flex-start', fontSize: 12, padding: '5px 10px', cursor: 'pointer',
            borderRadius: 6, border: '1px solid #e24b4a', background: 'transparent', color: '#e24b4a',
          }}>↑ Re-upload file</button>
        )}
      </div>
    </div>
  );
}

export default function HookManager({ featureKey, featureLabel }) {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const reuploadTarget = useRef(null);
  const renameTimers = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/hooks?withHealth=true`);
      setHooks(Array.isArray(res.data) ? res.data : []);
    } catch { setHooks([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const matchesFeature = (h) => {
    const tags = h.tags || [];
    if (!featureKey || tags.length === 0) return true;
    return tags.includes('all') || tags.includes('Universal') || tags.includes(featureKey);
  };
  const visible = hooks.filter(matchesFeature);

  const patchHook = async (filename, patch) => {
    setHooks(prev => prev.map(h => h.serverFilename === filename ? { ...h, ...patch } : h));
    try { await axios.patch(`${API}/api/hook/${filename}`, patch); } catch { load(); }
  };
  const onRename = (filename, name) => {
    setHooks(prev => prev.map(h => h.serverFilename === filename ? { ...h, name } : h));
    clearTimeout(renameTimers.current[filename]);
    renameTimers.current[filename] = setTimeout(() => {
      axios.patch(`${API}/api/hook/${filename}`, { name }).catch(() => {});
    }, 600);
  };
  const onSetType = (h, t) => patchHook(h.serverFilename, { hookType: t });
  const onToggleUniversal = (h) => {
    const isUniversal = (h.tags || []).includes('all') || (h.tags || []).includes('Universal');
    patchHook(h.serverFilename, { tags: isUniversal ? [featureKey] : ['all'] });
  };
  const onDelete = async (filename) => {
    if (!window.confirm('Delete this hook?')) return;
    setHooks(prev => prev.filter(h => h.serverFilename !== filename));
    try { await axios.delete(`${API}/api/hook/${filename}`); } catch { load(); }
  };

  const onReupload = (h) => { reuploadTarget.current = h; fileInputRef.current?.click(); };
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError('');
    const target = reuploadTarget.current; reuploadTarget.current = null;
    const fd = new FormData();
    fd.append('hook', file);
    fd.append('name', target ? target.name : file.name.replace(/\.[^.]+$/, ''));
    fd.append('hookType', target ? (target.hookType || 'visual') : 'visual');
    fd.append('tags', JSON.stringify(target ? (target.tags || [featureKey]) : [featureKey]));
    try { await axios.post(`${API}/api/upload-hook`, fd); load(); }
    catch (err) { setUploadError(err.response?.data?.message || 'Upload failed.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>💡 Tip · use clips under 10s</span>
        <button onClick={() => { reuploadTarget.current = null; fileInputRef.current?.click(); }}
          style={{ fontSize: 12, padding: '5px 10px', cursor: 'pointer', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent' }}>
          + Add hook
        </button>
      </div>

      {uploadError && (
        <div style={{ fontSize: 12, color: '#e24b4a', background: 'rgba(226,75,74,0.08)', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>{uploadError}</div>
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>Loading hooks…</div>
      ) : visible.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>No hooks for {featureLabel || 'this feature'} yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map(h => (
            <HookRow key={h.serverFilename} hook={h}
              onRename={onRename} onSetType={onSetType} onToggleUniversal={onToggleUniversal}
              onDelete={onDelete} onReupload={onReupload} featureLabel={featureLabel} />
          ))}
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="video/*" onChange={onFilePicked} style={{ display: 'none' }} />
    </div>
  );
}
